import React, { useEffect, useMemo } from 'react';
import { ScrollView, StyleSheet } from 'react-native';

import { EmptyState } from '~/components/core';
import { Position } from '~/api-services/LoadService/types';
import { scaleSize } from '~/constants/scale';
import { useAppDispatch, useAppSelector, useOpenTradesData, useOpenTradesFeed, useStyles } from '~/hooks';
import { accountBalanceSelector, symbolsSelector } from '~/state/selectors';
import { loadSymbols } from '~/state/reducers/symbolsSlice';
import { Theme } from '~/theme';

import PositionItem from '../../../OpenTrades/PositionItem';

type Props = {
    strategyId: string;
};

// No symbol grouping: a strategy trades one instrument, so a group header would only restate
// the row beneath it.
export default function LivePositions({ strategyId }: Props) {
    const styles = useStyles(stylesGenerator);
    const dispatch = useAppDispatch();
    const { positions, setOpenSymbol } = useOpenTradesData();
    const symbols = useAppSelector(symbolsSelector);
    const currency = useAppSelector(accountBalanceSelector)?.currency || '';
    useOpenTradesFeed();

    const mine = useMemo(
        () =>
            Object.values(positions as { [symbol: string]: Position[] })
                .flat()
                .filter((p) => p.strategyUid === strategyId),
        [positions, strategyId]
    );

    const missingSymbols = useMemo(
        () => [...new Set(mine.map((p) => p.symbol))].filter((s) => !symbols[s]),
        [mine, symbols]
    );

    useEffect(() => {
        if (missingSymbols.length) {
            dispatch(loadSymbols(missingSymbols));
        }
    }, [dispatch, missingSymbols]);

    // PositionItem prices off the live quote, which only ticks for the subscribed symbol. There
    // is no group to expand here, so subscribe the strategy's own instrument.
    const primarySymbol = mine.length ? mine[0].symbol : null;
    useEffect(() => {
        setOpenSymbol(primarySymbol);
    }, [primarySymbol, setOpenSymbol]);

    if (mine.length === 0) {
        return <EmptyState title="ai-strategy-live-no-position" body="ai-strategy-live-no-position-body" />;
    }

    return (
        <ScrollView style={styles.container}>
            {mine.map((position, index) =>
                symbols[position.symbol] ? (
                    <PositionItem
                        key={position.id}
                        position={position}
                        dataSymbol={symbols[position.symbol]}
                        currency={currency}
                        lastItem={index === mine.length - 1}
                        // Strategy positions are always own-account, never copy-trading.
                        investorUid={null}
                    />
                ) : null
            )}
        </ScrollView>
    );
}

const stylesGenerator = (_theme: Theme) =>
    StyleSheet.create({
        container: { paddingTop: scaleSize(7), paddingHorizontal: scaleSize(7) },
    });
